import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EulaDialogComponent } from '@components';
import { EULA, EULAAcceptance } from '@models';
import { CommonFunctionsService } from '@services';
import { environment } from 'environments/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { LoggerService } from '../logger/logger.service';

@Injectable({
  providedIn: 'root'
})
export class EulaService {
  private http: HttpClient = inject(HttpClient);
  private dialog: MatDialog = inject(MatDialog);
  private commonFunctionsService: CommonFunctionsService = inject(CommonFunctionsService);

  private logger = inject(LoggerService).setContext('EulaService');
  private eulaTitle = 'End user license agreement';
  BASE_URL = environment.CRG_DIGITALIZATION_SERVICE_URL;
  EULA_VALIDATION_URL = `${this.BASE_URL}/v1/eula/validation`;
  EULA_ACCEPTANCE_URL = `${this.BASE_URL}/v1/eula/accept`;

  checkUserEulaAcceptance(): Observable<EULA> {
    return this.commonFunctionsService.getRequestCommonParams(['user_id', 'principal_id']).pipe(
      switchMap((commonParams) =>
        this.http.get<EULA>(this.EULA_VALIDATION_URL, {
          observe: 'response',
          params: commonParams,
          withCredentials: true
        })
      ),
      map((response) => (response as any).body as EULA),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('Error checking EULA acceptance:', error);
        return throwError(() => new Error('ErrorFetchingEulaAcceptance'));
      })
    );
  }

  saveEulaAcceptance(eulaId: number): Observable<EULAAcceptance> {
    return this.http
      .post(
        this.EULA_ACCEPTANCE_URL,
        { eula_id: eulaId },
        {
          observe: 'response',
          withCredentials: true
        }
      )
      .pipe(
        map((response) => response.body as EULAAcceptance),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('Error saving EULA acceptance:', error);
          return throwError(() => new Error('ErrorSavingEulaAcceptance'));
        })
      );
  }

  saveEulaAcceptanceFromGet(eulaId: number): Observable<EULAAcceptance> {
    return this.commonFunctionsService.getRequestCommonParams(['user_id', 'principal_id']).pipe(
      map((commonParams) => commonParams.set('eula_id', eulaId.toString())),
      switchMap((commonParams) =>
        this.http.get(this.EULA_ACCEPTANCE_URL, {
          observe: 'response',
          params: commonParams,
          withCredentials: true
        })
      ),
      map((response) => (response as any).body as EULAAcceptance),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('Error saving EULA acceptance:', error);
        return throwError(() => new Error('ErrorSavingEulaAcceptance'));
      })
    );
  }

  showEulaDialog(eulaData: EULA, usePost = false, reviewOnly = false): Observable<boolean> {
    const dialogRef: MatDialogRef<EulaDialogComponent, boolean> = this.dialog.open(
      EulaDialogComponent,
      {
        data: {
          eulaContent: eulaData.currentEula.documentContent,
          eulaTitle: this.eulaTitle,
          eulaId: eulaData.currentEula.id,
          eulaVersion: eulaData.currentEula.version,
          usePost: usePost,
          reviewOnly: reviewOnly,
          acceptedAt: eulaData.acceptanceDate ?? null
        },
        disableClose: true,
        panelClass: 'eula-dialog-panel',
        height: '626px'
      }
    );

    return dialogRef.afterClosed().pipe(map((result) => result === true));
  }

  isCurrentEulaAccepted(eulaCheck: EULA): boolean {
    if (!eulaCheck.hasAcceptedCurrent) return false;
    this.setEulaAcceptanceInSession(eulaCheck.hasAcceptedCurrent);
    return true;
  }

  isCurrentEulaAcceptedInSession(): boolean {
    return this.getEulaAcceptanceFromSession();
  }

  storeEulaAcceptance(acceptance: boolean): void {
    this.setEulaAcceptanceInSession(acceptance);
  }

  setShowSuccessNotification(show: boolean): void {
    sessionStorage.setItem('eulaSuccessNotification', show ? 'true' : 'false');
  }

  shouldShowSuccessNotification(): boolean {
    return sessionStorage.getItem('eulaSuccessNotification') === 'true';
  }

  clearSuccessNotification(): void {
    sessionStorage.removeItem('eulaSuccessNotification');
  }

  setEulaVersion(version: string): void {
    sessionStorage.setItem('eulaVersion', version);
  }

  getEulaVersion(): string {
    return sessionStorage.getItem('eulaVersion') || '';
  }

  clearEulaVersion(): void {
    sessionStorage.removeItem('eulaVersion');
  }

  private getEulaAcceptanceFromSession(): boolean {
    return sessionStorage.getItem('eulaAccepted') === 'true';
  }
  private setEulaAcceptanceInSession(acceptance: boolean): void {
    sessionStorage.setItem('eulaAccepted', acceptance ? 'true' : 'false');
  }
}
